import React, { useState, useMemo } from 'react';
import { PERMISSION_GROUPS, ALL_PERMISSIONS } from '../../utils/permissions';

/**
 * PermissionManager Component
 * 
 * Visual permission editor with grouped checkboxes.
 * Shows role-based permissions with optional user-specific overrides.
 * 
 * @param {Object} props
 * @param {Array<string>} props.rolePermissions - Base permissions from role
 * @param {Array<string>} props.permissionGrants - User-specific grants (optional)
 * @param {Array<string>} props.permissionDenials - User-specific denials (optional)
 * @param {Function} props.onPermissionChange - Callback(permission, action) where action is 'grant', 'deny', or 'default'
 * @param {boolean} props.isAdmin - Whether current user is admin
 * @param {boolean} props.showOverrides - Show grant/deny columns (for per-user customization)
 * @param {boolean} props.readOnly - Disable all checkboxes
 */
const PermissionManager = ({
  rolePermissions = [],
  permissionGrants = [],
  permissionDenials = [],
  onPermissionChange,
  onBulkPermissionChange,
  isAdmin = false,
  showOverrides = false,
  readOnly = false,
}) => {
  const [expandedGroups, setExpandedGroups] = useState(
    // Expand all groups by default
    PERMISSION_GROUPS.reduce((acc, group, idx) => ({ ...acc, [idx]: true }), {})
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all'); // all, page, feature, action

  // Toggle group expansion
  const toggleGroup = (groupIndex) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupIndex]: !prev[groupIndex],
    }));
  };

  // Expand/collapse all groups
  const expandAll = () => {
    const expanded = PERMISSION_GROUPS.reduce((acc, _, idx) => ({ ...acc, [idx]: true }), {});
    setExpandedGroups(expanded);
  };

  const collapseAll = () => {
    setExpandedGroups({});
  };

  // Filter permissions based on search and level filter
  const filteredGroups = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    
    return PERMISSION_GROUPS.map(group => {
      const filtered = group.permissions.filter(perm => {
        // Level filter
        if (filterLevel !== 'all') {
          const prefix = `${filterLevel}:`;
          if (!perm.key.startsWith(prefix)) return false;
        }
        
        // Search filter
        if (searchTerm) {
          const matchesLabel = perm.label.toLowerCase().includes(searchLower);
          const matchesDesc = perm.description.toLowerCase().includes(searchLower);
          const matchesKey = perm.key.toLowerCase().includes(searchLower);
          return matchesLabel || matchesDesc || matchesKey;
        }
        
        return true;
      });
      
      return { ...group, permissions: filtered };
    }).filter(group => group.permissions.length > 0);
  }, [searchTerm, filterLevel]);

  // Get permission status (from role, granted, or denied)
  const getPermissionStatus = (permissionKey) => {
    if (permissionDenials.includes(permissionKey)) return 'denied';
    if (permissionGrants.includes(permissionKey)) return 'granted';
    if (rolePermissions.includes(permissionKey)) return 'role';
    return 'none';
  };

  // Handle permission checkbox change
  const handlePermissionToggle = (permissionKey) => {
    if (readOnly || !onPermissionChange) return;
    
    const status = getPermissionStatus(permissionKey);
    const isInRole = rolePermissions.includes(permissionKey);
    
    if (showOverrides) {
      // Three-state cycle, but skip states that would duplicate the role permission
      if (status === 'role') {
        // Role permission clicked → deny it
        onPermissionChange(permissionKey, 'deny');
      } else if (status === 'denied') {
        // Denied permission clicked → grant it
        onPermissionChange(permissionKey, 'grant');
      } else if (status === 'granted') {
        // Granted permission clicked → revert to default
        // If permission is in role, it will become 'role' after removal
        // If not in role, it will become 'none' after removal
        onPermissionChange(permissionKey, 'default');
      } else {
        // None (not in role, not granted, not denied) → grant it
        onPermissionChange(permissionKey, 'grant');
      }
    } else {
      // Simple toggle for role editing
      if (status === 'role' || status === 'granted') {
        onPermissionChange(permissionKey, 'remove');
      } else {
        onPermissionChange(permissionKey, 'add');
      }
    }
  };

  // Handle group-level select/deselect all
  const handleGroupToggle = (group) => {
    if (readOnly || !onBulkPermissionChange) return;

    const permKeys = group.permissions.map(p => p.key);
    const allActive = permKeys.every(key => {
      const status = getPermissionStatus(key);
      return status === 'role' || status === 'granted';
    });

    if (showOverrides) {
      onBulkPermissionChange(permKeys, allActive ? 'deny' : 'grant');
    } else {
      onBulkPermissionChange(permKeys, allActive ? 'remove' : 'add');
    }
  };

  // Count permissions
  const totalPermissions = Object.keys(ALL_PERMISSIONS).length;
  const activePermissions = showOverrides
    ? (() => {
        // Start with role permissions
        const active = new Set(rolePermissions);
        // Remove denials
        permissionDenials.forEach(p => active.delete(p));
        // Add grants (only if not already in role)
        permissionGrants.forEach(p => active.add(p));
        return active.size;
      })()
    : rolePermissions.length;

  return (
    <div className="permission-manager bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Permissions</h3>
            <p className="text-sm text-gray-600">
              {activePermissions} of {totalPermissions} permissions enabled
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="text-sm text-indigo-600 hover:text-indigo-700 px-3 py-1 rounded hover:bg-indigo-50"
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="text-sm text-indigo-600 hover:text-indigo-700 px-3 py-1 rounded hover:bg-indigo-50"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search permissions..."
            className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="page">Pages Only</option>
            <option value="feature">Features Only</option>
            <option value="action">Actions Only</option>
          </select>
        </div>
      </div>

      {/* Permission Groups */}
      <div className="max-h-[600px] overflow-y-auto">
        {filteredGroups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No permissions match your search.
          </div>
        ) : (
          filteredGroups.map((group, groupIndex) => {
            const originalIndex = PERMISSION_GROUPS.findIndex(g => g.label === group.label);
            const isExpanded = expandedGroups[originalIndex];
            
            // Count active permissions in this group
            const groupActiveCount = group.permissions.filter(p => {
              const status = getPermissionStatus(p.key);
              return status === 'role' || status === 'granted';
            }).length;

            return (
              <div key={group.label} className="border-b last:border-b-0">
                {/* Group Header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(originalIndex)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        isExpanded ? 'transform rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    {!readOnly && onBulkPermissionChange && (
                      <input
                        type="checkbox"
                        ref={el => { if (el) el.indeterminate = groupActiveCount > 0 && groupActiveCount < group.permissions.length; }}
                        checked={groupActiveCount === group.permissions.length}
                        onClick={e => e.stopPropagation()}
                        onChange={() => handleGroupToggle(group)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        title={groupActiveCount === group.permissions.length ? 'Deselect all in group' : 'Select all in group'}
                      />
                    )}
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900">{group.label}</h4>
                      <p className="text-sm text-gray-600">{group.description}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-600">
                    {groupActiveCount} / {group.permissions.length}
                  </span>
                </button>

                {/* Group Permissions */}
                {isExpanded && (
                  <div className="px-4 py-2 bg-gray-50">
                    {group.permissions.map((perm) => {
                      const status = getPermissionStatus(perm.key);
                      const isChecked = status === 'role' || status === 'granted';
                      const isDenied = status === 'denied';

                      return (
                        <label
                          key={perm.key}
                          className={`flex items-center gap-3 py-2 px-3 rounded hover:bg-white transition-colors cursor-pointer ${
                            isDenied ? 'opacity-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handlePermissionToggle(perm.key)}
                            disabled={readOnly}
                            className={`w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 ${
                              readOnly ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {perm.label}
                              </span>
                              {status === 'granted' && showOverrides && (
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                  Granted
                                </span>
                              )}
                              {isDenied && showOverrides && (
                                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                  Denied
                                </span>
                              )}
                              {status === 'role' && showOverrides && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  From Role
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600">{perm.description}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{perm.key}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      {showOverrides && (
        <div className="p-4 border-t bg-gray-50">
          <p className="text-xs text-gray-600 font-semibold mb-2">Legend:</p>
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">From Role</span>
              <span>- Permission from user's role</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">Granted</span>
              <span>- User-specific grant (overrides role)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">Denied</span>
              <span>- User-specific denial (overrides role)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManager;
